
import React, { useState } from 'react';
import { MapPin, ArrowRight, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { PROJECTS, TESTIMONIALS } from '../data';

const Projekter = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const openProject = (project) => {
    setSelectedProject(project);
    setCurrentImageIndex(0);
    document.body.style.overflow = 'hidden';
  };

  const closeProject = () => {
    setSelectedProject(null);
    document.body.style.overflow = 'auto';
  };

  const nextImage = (e) => {
    e.stopPropagation();
    if (selectedProject) {
      setCurrentImageIndex((prev) => (prev + 1) % selectedProject.images.length);
    }
  };

  const prevImage = (e) => {
    e.stopPropagation();
    if (selectedProject) {
      setCurrentImageIndex((prev) => (prev - 1 + selectedProject.images.length) % selectedProject.images.length);
    }
  };

  return (
    <Layout>
      <section className="pt-56 pb-24 bg-slate-900 overflow-hidden relative">
        <div className="absolute inset-0 flex items-center justify-center text-[20vw] font-bold text-white/[0.02] select-none pointer-events-none leading-none z-0 uppercase tracking-tighter">
          GALLERI
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h1 className="text-[1rem] md:text-8xl font-bold text-white tracking-tighter mb-6">VORES PROJEKTER</h1>
            <p className="text-amber-500 font-bold tracking-[0.3em] uppercase">Se et udpluk af vores arbejde i Nordjylland</p>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {PROJECTS.map((project) => (
              <div 
                key={project.id}
                className="group cursor-pointer"
                onClick={() => openProject(project)}
              >
                <div className="relative aspect-[4/5] overflow-hidden mb-6 bg-slate-100 shadow-xl rounded-lg">
                  <img 
                    src={project.image} 
                    alt={project.title} 
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute bottom-6 left-6 text-white opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-4 group-hover:translate-y-0">
                    <p className="text-xs font-bold uppercase tracking-widest text-amber-500 mb-1">Se galleri</p>
                    <h3 className="text-xl font-bold tracking-tight">{project.title}</h3>
                  </div>
                </div>
                <div className="flex justify-between items-start px-2">
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 tracking-tight group-hover:text-amber-500 transition-colors">{project.title}</h3>
                    <div className="flex items-center gap-2 text-slate-500 text-sm mt-1 uppercase tracking-widest font-bold">
                      <MapPin size={14} className="text-amber-500" />
                      {project.location}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Project Gallery Modal */}
      {selectedProject && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/95 p-4 md:p-10"
          onClick={closeProject}
        >
          <button 
            className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors z-[110]"
            onClick={closeProject}
          >
            <X size={40} />
          </button>

          <div className="relative w-full max-w-6xl h-full flex flex-col items-center justify-center gap-8" onClick={(e) => e.stopPropagation()}>
            <div className="relative w-full flex-1 flex items-center justify-center group">
              <button 
                className="absolute left-0 text-white/50 hover:text-white transition-colors p-4 z-10"
                onClick={prevImage}
              >
                <ChevronLeft size={48} />
              </button>
              
              <img 
                src={selectedProject.images[currentImageIndex]} 
                alt={`${selectedProject.title} - ${currentImageIndex + 1}`}
                className="max-w-full max-h-full object-contain shadow-2xl rounded-lg"
              />

              <button 
                className="absolute right-0 text-white/50 hover:text-white transition-colors p-4 z-10"
                onClick={nextImage}
              >
                <ChevronRight size={48} />
              </button>
            </div>

            <div className="text-center text-white">
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-2">{selectedProject.title}</h2>
              <p className="text-amber-500 font-bold uppercase tracking-widest text-sm flex items-center justify-center gap-2">
                <MapPin size={16} /> {selectedProject.location}
              </p>
              <div className="flex gap-2 mt-6 justify-center">
                {selectedProject.images.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImageIndex(idx)}
                    className={`w-2 h-2 rounded-full transition-all ${currentImageIndex === idx ? 'bg-amber-500 w-8' : 'bg-white/20 hover:bg-white/40'}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Testimonials in Projects page */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-amber-500 font-bold text-xs tracking-widest uppercase mb-4">Det siger de om os</h2>
            <h3 className="text-4xl font-bold text-slate-900 tracking-tight uppercase">Kunderne fortæller</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {TESTIMONIALS.map((t) => (
              <div key={t.id} className="bg-white p-10 shadow-sm border border-slate-100 relative">
                <div className="text-amber-500 mb-6">
                  {[...Array(5)].map((_, i) => <span key={i} className="text-xl">★</span>)}
                </div>
                <p className="text-slate-600 italic mb-8 leading-relaxed">"{t.text}"</p>
                <div>
                  <h4 className="font-bold text-slate-900">{t.name}</h4>
                  <p className="text-slate-400 text-xs uppercase tracking-widest mt-1">{t.project}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-900 text-white border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tighter mb-8 leading-none uppercase">SKAL VI SKABE DIT NÆSTE PROJEKT?</h2>
          <p className="text-slate-400 max-w-2xl mx-auto mb-12 text-lg font-light leading-relaxed">
            Vi kommer gerne ud og kigger på dit projekt og giver et uforpligtende tilbud. Ingen opgave er for lille eller for stor.
          </p>
          <Link to="/kontakt" className="px-12 py-5 bg-amber-500 text-white font-bold hover:bg-amber-600 transition-colors uppercase tracking-widest text-sm inline-flex items-center gap-3">
            Kontakt os i dag <ArrowRight size={20} />
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default Projekter;
